import React, {useEffect, useMemo, useRef, useState} from 'react';
import type {SuggestionProps} from '@tiptap/suggestion';
import {Spinner} from '@time-webkit/all/atoms/spinner';
import {ListBox, ListBoxItem} from '@time-webkit/all/atoms/listbox';
import {Elevation} from '@time-webkit/all/atoms/elevation';
import {PopoverOverlay} from '@time-webkit/all/molecules/popover';
import classNames from 'classnames';
import Constants from 'utils/constants';
import {useEventCallback} from 'usehooks-ts';

import styles from './styles.module.css';
import {MentionView, useGetMentions} from './use-get-mentions';
import type {Mention} from './types';
import {LegacyTryAgain} from './legacy-try-again';

/* 8px top padding, 4 list items and just enough of 5th */
const DEFAULT_LIST_MAX_HEIGHT = 8 + (36 * 4) + (36 * 0.6);

type LegacyMentionListProps<M extends Mention> = {
    getMentions: (query: string) => Promise<M[]>;
    renderMentionOption: (mention: M) => React.ReactNode;
    onSelect: (mention: M, index: number) => void;
    maxHeight?: number;
    calcMaxHeight?: (mentions: M[]) => number;
    ignoreEmptyQuery?: boolean;
} & Pick<SuggestionProps<M>, 'query' | 'command' | 'decorationNode'>;

export const LegacyMentionList = <M extends Mention, >(props: LegacyMentionListProps<M>) => {
    const {
        query, getMentions, command, decorationNode, calcMaxHeight,
        renderMentionOption, onSelect, maxHeight = DEFAULT_LIST_MAX_HEIGHT,
        ignoreEmptyQuery = true,
    } = props;

    const {loading, view, mentions, retry} = useGetMentions<M>(query, getMentions, ignoreEmptyQuery);

    const listBoxRef = useRef<HTMLUListElement>(null);

    const [selected, setSelected] = useState(0);

    const shouldShowPopover = mentions.length > 0 || loading;
    const [isOpen, setOpen] = useState(shouldShowPopover);

    const mentionsKey = useMemo(() => {
        return mentions.map((mention) => mention.handle).toString();
    }, [mentions]);

    useEffect(() => {
        setSelected(0);
    }, [mentionsKey]);

    const onKeyDown = useEventCallback((e: KeyboardEvent) => {
        // Если результатов нет, то список эмоджей не отображается и обрабатывать ничего не нужно
        if (!mentions.length) {
            return;
        }

        if (e.key === Constants.KeyCodes.UP[0]) {
            e.preventDefault();
            e.stopPropagation();

            if (selected === 0) {
                return;
            }
            setSelected(selected - 1);
        }

        if (e.key === Constants.KeyCodes.DOWN[0]) {
            e.preventDefault();
            e.stopPropagation();

            if (selected === mentions.length - 1) {
                return;
            }
            setSelected(selected + 1);
        }

        if (e.key === Constants.KeyCodes.ENTER[0]) {
            handleSelect(new Set([mentions[selected]?.handle as string]));
            e.preventDefault();
            e.stopPropagation();
        }
    });

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        listBoxRef.current?.children.item(selected)?.scrollIntoView?.({behavior: 'instant', block: 'nearest'});
    }, [selected]);

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [onKeyDown]);

    useEffect(() => {
        if (mentions.length > 0 || loading) {
            setOpen(true);
        }
    }, [mentions, loading]);

    const handleSelect = React.useCallback<Required<React.ComponentProps<typeof ListBox>>['onSelectionChange']>(
        (keys) => {
            for (const entry of keys) {
                const foundMentionIndex = mentions.findIndex((mention) => mention.handle === entry);
                const foundMention = mentions[foundMentionIndex];

                if (foundMention) {
                    onSelect(foundMention, foundMentionIndex);
                    command(foundMention);
                }
            }
        },
        [command, mentions, onSelect],
    );

    if (!shouldShowPopover) {
        /**
         * В маттермосте в такой ситуации вообще не показывается меню
         */
        return null;
    }

    const wrapWithPopoverOverlay = (children: React.ReactNode, maxHeight?: number) => (
        <PopoverOverlay
            isOpen={isOpen}
            onOpenChange={setOpen}
            zIndex={999}
            triggerNode={decorationNode ?? undefined}
            placement='top start'
            maxHeight={maxHeight}
        >
            {children}
        </PopoverOverlay>
    );

    if (view === MentionView.LOADER_VIEW) {
        return (
            wrapWithPopoverOverlay(
                <Elevation>
                    <div className={styles.elevation}>
                        <Spinner />
                    </div>
                </Elevation>,
            )
        );
    }

    if (view === MentionView.RETRY_VIEW) {
        return (
            wrapWithPopoverOverlay(
                <Elevation>
                    <div className={styles.elevation}>
                        <LegacyTryAgain retry={retry} />
                    </div>
                </Elevation>,
            )
        );
    }

    return (
        wrapWithPopoverOverlay(
            <ListBox
                key={selected}
                data-testid='mention-list'
                style={{
                    maxHeight: calcMaxHeight ? calcMaxHeight(mentions) : maxHeight,
                }}
                className={styles.listWrapper}
                onSelectionChange={handleSelect}
                items={mentions}
                ref={listBoxRef}
                selectionMode='single'
                shouldFocusWrap={true}
                aria-label='List'
            >
                {(item: M) => {
                    return (
                        <ListBoxItem key={item.handle} aria-label={item.displayName || item.handle}>
                            <div
                                className={classNames({
                                    [styles.optionFocused]: item.handle === mentions[selected]?.handle})
                                }
                            >
                                {renderMentionOption(item)}
                            </div>
                        </ListBoxItem>
                    );
                }}
            </ListBox>,
            maxHeight,
        )
    );
};
